import React, { createContext, useContext, useEffect, useCallback, useState, ReactNode } from 'react';
import { message } from 'antd';
import { socketService } from '../../services/socket';

interface SocketContextValue {
    connected: boolean;
    connecting: boolean;
    error: Error | null;
    connect: () => void;
    disconnect: () => void;
    /** Subscribe to vehicles by name list (call after fetching vehicle list) */
    subscribeVehicles: (vehicleNames: string[]) => void;
}

const SocketContext = createContext<SocketContextValue | null>(null);

interface SocketProviderProps {
    children: ReactNode;
    autoConnect?: boolean;
    showNotifications?: boolean;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({
    children,
    autoConnect = true,
    showNotifications = true,
}) => {
    const [connected, setConnected] = useState(socketService.isConnected());
    const [connecting, setConnecting] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        socketService.onConnect(() => {
            setConnected(true);
            setConnecting(false);
            setError(null);
            if (showNotifications) {
                message.success('实时连接已建立');
            }
        });

        socketService.onDisconnect((reason) => {
            setConnected(false);
            if (showNotifications && reason !== 'io client disconnect') {
                message.warning('实时连接已断开');
            }
        });

        socketService.onError((err) => {
            setConnecting(false);
            setError(err);
            console.error('[SocketProvider] Connection error:', err);
        });

        if (autoConnect && !socketService.isConnected()) {
            setConnecting(true);
            socketService.connect();
        }

        return () => {
            socketService.disconnect();
        };
    }, [autoConnect, showNotifications]);

    const connect = useCallback(() => {
        if (!socketService.isConnected()) {
            setConnecting(true);
            socketService.connect();
        }
    }, []);

    const disconnect = useCallback(() => {
        socketService.disconnect();
        setConnected(false);
    }, []);

    const subscribeVehicles = useCallback((vehicleNames: string[]) => {
        socketService.subscribeVehicles(vehicleNames);
    }, []);

    const value: SocketContextValue = {
        connected,
        connecting,
        error,
        connect,
        disconnect,
        subscribeVehicles,
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};

/**
 * 使用 Socket 上下文
 */
export function useSocketContext(): SocketContextValue {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocketContext must be used within a SocketProvider');
    }
    return context;
}

export default SocketProvider;
